import type { User } from "firebase/auth";
import {
  collection,
  addDoc,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  type Timestamp,
  type Unsubscribe
} from "firebase/firestore";
import type { ServiceCatalogItem, ServiceRequest } from "../types/imobi";
import { db } from "./firebase";

export type SaveServiceCatalogInput = {
  id?: string;
  name: string;
  description: string;
  billingType: ServiceCatalogItem["billingType"];
  baseValue: number;
  minimumValue: number;
  minimumQuantity: number;
  valuePerAsset: number;
  maxDiscountPercent: number;
  maxAssets: number;
  maxUsers: number;
  allowDirectCheckout: boolean;
  hidePrice: boolean;
  serviceCategory: string;
  suggestedRange: string;
  quoteValidityDays: number;
  adjustmentType: "none" | "discount" | "increase";
  adjustmentValue: number;
  requiresMasterApproval: boolean;
  releasesAutomatically: boolean;
  status: ServiceCatalogItem["status"];
  internalNotes?: string;
};

export type ServiceRequestRecord = ServiceRequest & {
  companyName?: string;
  serviceName?: string;
  requesterEmail?: string;
  estimatedAmount?: number;
  commercialStatus?: string;
  paymentProvider?: string;
  paymentPreferenceId?: string;
  quoteAmount?: number;
  quoteNotes?: string;
  quoteValidUntil?: string;
  serviceCategory?: string;
  adjustmentType?: string;
  adjustmentValue?: number;
};

function serializeTimestamp(value: unknown): string {
  if (value && typeof value === "object" && "toDate" in value) {
    return (value as Timestamp).toDate().toISOString();
  }

  return typeof value === "string" ? value : "";
}

function normalizeServiceId(value: string) {
  return value
    .trim()
    .toLocaleLowerCase("pt-BR")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

function numberOrZero(value: unknown) {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function mapCatalogItem(id: string, data: Record<string, unknown>): ServiceCatalogItem {
  return {
    id,
    name: String(data.name ?? ""),
    description: String(data.description ?? ""),
    billingType: (data.billingType ?? "personalizado") as ServiceCatalogItem["billingType"],
    baseValue: numberOrZero(data.baseValue),
    minimumValue: numberOrZero(data.minimumValue),
    minimumQuantity: numberOrZero(data.minimumQuantity),
    valuePerAsset: numberOrZero(data.valuePerAsset),
    valuePerBranch: numberOrZero(data.valuePerBranch),
    valuePerAdditionalUser: numberOrZero(data.valuePerAdditionalUser),
    maxDiscountPercent: numberOrZero(data.maxDiscountPercent),
    maxAssets: numberOrZero(data.maxAssets),
    maxUsers: numberOrZero(data.maxUsers),
    allowDirectCheckout: data.allowDirectCheckout === true,
    hidePrice: data.hidePrice === true,
    serviceCategory: (data.serviceCategory ?? "servico_avulso") as ServiceCatalogItem["serviceCategory"],
    suggestedRange: data.suggestedRange ? String(data.suggestedRange) : undefined,
    quoteValidityDays: numberOrZero(data.quoteValidityDays) || 7,
    adjustmentType: (data.adjustmentType ?? "none") as ServiceCatalogItem["adjustmentType"],
    adjustmentValue: numberOrZero(data.adjustmentValue),
    requiresMasterApproval: data.requiresMasterApproval !== false,
    releasesAutomatically: data.releasesAutomatically === true,
    status: data.status === "inativo" ? "inativo" : "ativo",
    internalNotes: data.internalNotes ? String(data.internalNotes) : undefined,
    createdAt: serializeTimestamp(data.createdAt),
    createdBy: String(data.createdBy ?? ""),
    updatedAt: serializeTimestamp(data.updatedAt),
    updatedBy: data.updatedBy ? String(data.updatedBy) : undefined
  };
}

function mapRequest(id: string, data: Record<string, unknown>): ServiceRequestRecord {
  return {
    id,
    companyId: String(data.companyId ?? ""),
    companyName: data.companyName ? String(data.companyName) : undefined,
    requesterUserId: String(data.requesterUserId ?? ""),
    requesterEmail: data.requesterEmail ? String(data.requesterEmail) : undefined,
    serviceId: String(data.serviceId ?? ""),
    serviceName: data.serviceName ? String(data.serviceName) : undefined,
    description: String(data.description ?? ""),
    estimatedAssetsCount: numberOrZero(data.estimatedAssetsCount),
    urgency: (data.urgency ?? "normal") as ServiceRequest["urgency"],
    attachmentUrls: Array.isArray(data.attachmentUrls) ? data.attachmentUrls.map(String) : [],
    status: (data.status ?? "solicitado") as ServiceRequest["status"],
    commercialStatus: data.commercialStatus ? String(data.commercialStatus) : undefined,
    estimatedAmount: numberOrZero(data.estimatedAmount),
    paymentProvider: data.paymentProvider ? String(data.paymentProvider) : undefined,
    paymentPreferenceId: data.paymentPreferenceId ? String(data.paymentPreferenceId) : undefined,
    quoteAmount: numberOrZero(data.quoteAmount),
    quoteNotes: data.quoteNotes ? String(data.quoteNotes) : undefined,
    quoteValidUntil: serializeTimestamp(data.quoteValidUntil),
    serviceCategory: data.serviceCategory ? String(data.serviceCategory) : undefined,
    adjustmentType: data.adjustmentType ? String(data.adjustmentType) : undefined,
    adjustmentValue: numberOrZero(data.adjustmentValue),
    createdAt: serializeTimestamp(data.createdAt),
    createdBy: String(data.createdBy ?? ""),
    updatedAt: serializeTimestamp(data.updatedAt),
    updatedBy: data.updatedBy ? String(data.updatedBy) : undefined
  };
}

export function calculateServiceEstimate(service: ServiceCatalogItem, quantity: number) {
  const effectiveQuantity = Math.max(quantity, service.minimumQuantity ?? 1, 1);
  const variableValue = service.billingType === "por_bem" ? (service.valuePerAsset ?? 0) * effectiveQuantity : 0;
  return Math.max((service.baseValue ?? 0) + variableValue, service.minimumValue ?? 0, 1);
}

export function applyCommercialAdjustment(value: number, adjustmentType?: string, adjustmentValue?: number) {
  const adjustment = adjustmentValue ?? 0;
  if (adjustmentType === "discount") {
    return Math.max(value - adjustment, 0);
  }
  if (adjustmentType === "increase") {
    return value + adjustment;
  }
  return value;
}

export function subscribeServiceCatalog(onChange: (items: ServiceCatalogItem[]) => void, onError: (message: string) => void): Unsubscribe {
  if (!db) {
    onChange([]);
    return () => undefined;
  }

  return onSnapshot(
    query(collection(db, "serviceCatalog"), orderBy("name")),
    (snapshot) => onChange(snapshot.docs.map((itemDoc) => mapCatalogItem(itemDoc.id, itemDoc.data()))),
    (error) => onError(error.message)
  );
}

export function subscribeServiceRequests(options: { userId?: string; admin: boolean }, onChange: (requests: ServiceRequestRecord[]) => void, onError: (message: string) => void): Unsubscribe {
  if (!db) {
    onChange([]);
    return () => undefined;
  }

  const requestQuery = options.admin
    ? query(collection(db, "serviceRequests"), orderBy("createdAt", "desc"), limit(80))
    : query(collection(db, "serviceRequests"), where("requesterUserId", "==", options.userId ?? ""), limit(40));

  return onSnapshot(
    requestQuery,
    (snapshot) => onChange(snapshot.docs
      .map((requestDoc) => mapRequest(requestDoc.id, requestDoc.data()))
      .sort((left, right) => {
        const leftTime = left.createdAt ? new Date(left.createdAt).getTime() : 0;
        const rightTime = right.createdAt ? new Date(right.createdAt).getTime() : 0;
        return rightTime - leftTime;
      })),
    (error) => onError(error.message)
  );
}

export async function saveServiceCatalogItem(input: SaveServiceCatalogInput, user: User) {
  if (!db) {
    throw new Error("Firestore nao configurado.");
  }

  const name = input.name.trim();
  const id = input.id || normalizeServiceId(name);
  if (!id || !name) {
    throw new Error("Informe o nome do servico.");
  }

  await setDoc(doc(db, "serviceCatalog", id), {
    name,
    description: input.description.trim(),
    billingType: input.billingType,
    baseValue: input.baseValue,
    minimumValue: input.minimumValue,
    minimumQuantity: input.minimumQuantity,
    valuePerAsset: input.valuePerAsset,
    maxDiscountPercent: input.maxDiscountPercent,
    maxAssets: input.maxAssets,
    maxUsers: input.maxUsers,
    allowDirectCheckout: input.allowDirectCheckout,
    hidePrice: input.hidePrice,
    serviceCategory: input.serviceCategory,
    suggestedRange: input.suggestedRange,
    quoteValidityDays: input.quoteValidityDays,
    adjustmentType: input.adjustmentType,
    adjustmentValue: input.adjustmentValue,
    requiresMasterApproval: input.requiresMasterApproval,
    releasesAutomatically: input.releasesAutomatically,
    status: input.status,
    internalNotes: input.internalNotes ?? "",
    updatedAt: serverTimestamp(),
    updatedBy: user.uid,
    createdAt: serverTimestamp(),
    createdBy: user.uid
  }, { merge: true });
}

export async function createServiceRequest(input: {
  companyId: string;
  companyName: string;
  service: ServiceCatalogItem;
  description: string;
  urgency: ServiceRequest["urgency"];
  quantity: number;
}, user: User): Promise<string> {
  if (!db) {
    throw new Error("Firestore nao configurado.");
  }

  const estimatedAmount = calculateServiceEstimate(input.service, input.quantity);
  const requestRef = await addDoc(collection(db, "serviceRequests"), {
    companyId: input.companyId,
    companyName: input.companyName,
    requesterUserId: user.uid,
    requesterEmail: user.email ?? "",
    serviceId: input.service.id,
    serviceName: input.service.name,
    serviceCategory: input.service.serviceCategory ?? "servico_avulso",
    description: input.description,
    estimatedAssetsCount: input.quantity,
    urgency: input.urgency,
    attachmentUrls: [],
    status: "solicitado",
    commercialStatus: "aguardando_orcamento",
    estimatedAmount,
    createdAt: serverTimestamp(),
    createdBy: user.uid,
    updatedAt: serverTimestamp(),
    updatedBy: user.uid
  });
  return requestRef.id;
}

export async function sendServiceQuote(request: ServiceRequestRecord, input: {
  quoteAmount: number;
  quoteNotes: string;
  quoteValidityDays: number;
  adjustmentType: "none" | "discount" | "increase";
  adjustmentValue: number;
}, user: User) {
  if (!db) {
    throw new Error("Firestore nao configurado.");
  }

  const validUntil = new Date();
  validUntil.setDate(validUntil.getDate() + Math.max(1, input.quoteValidityDays));
  const quoteAmount = applyCommercialAdjustment(input.quoteAmount, input.adjustmentType, input.adjustmentValue);

  await updateDoc(doc(db, "serviceRequests", request.id), {
    status: "orcamento_enviado",
    commercialStatus: "aguardando_cliente",
    quoteAmount,
    estimatedAmount: quoteAmount,
    quoteNotes: input.quoteNotes,
    quoteValidUntil: validUntil,
    adjustmentType: input.adjustmentType,
    adjustmentValue: input.adjustmentValue,
    updatedAt: serverTimestamp(),
    updatedBy: user.uid
  });
}

export async function releaseServiceManually(request: ServiceRequestRecord, user: User) {
  if (!db) {
    throw new Error("Firestore nao configurado.");
  }

  await updateDoc(doc(db, "serviceRequests", request.id), {
    status: "liberado",
    commercialStatus: "liberado_manual",
    manuallyReleasedBy: user.uid,
    manuallyReleasedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    updatedBy: user.uid
  });
}

export async function updateServiceRequestStatus(requestId: string, status: ServiceRequest["status"], user: User) {
  if (!db) {
    throw new Error("Firestore nao configurado.");
  }

  await updateDoc(doc(db, "serviceRequests", requestId), {
    status,
    commercialStatus: status === "cancelado" ? "recusado_cliente" : "aguardando_cliente",
    updatedAt: serverTimestamp(),
    updatedBy: user.uid
  });
}
